import { getArcade } from "./arcade";
import { gameById } from "../games/registry";
import type { MatchSummary } from "../types";
import type { LunaView } from "@arcade/luna-genesis";

type TurnSnapshot = { state: unknown; turn: string | null; deadline: number | null };

/**
 * Re-derive one match summary from the server's authoritative turn snapshot, returning an updated
 * copy when its status/scores changed and `null` otherwise (including on error — a single offline
 * or stale room must not disturb the rest of the list).
 *
 * The games list loads its summaries once and is only otherwise refreshed by opening a game (whose
 * <Table> feeds `adapter.summarize` back through `onProgress`). Without this, an opponent's move
 * never flips a card from "Waiting" to "Your turn" until you enter the match. We reuse the exact
 * same `summarize` derivation so the list and the table screen always agree.
 *
 * Only games with an actual move on record are re-derived. Before the first move the server
 * reports the host (seat 0) as the active turn even while it's still waiting for an opponent, so
 * deriving a status from a stateless snapshot would falsely promote a brand-new game to
 * "Your turn" — and that pre-game limbo is not the case this repairs.
 */
export async function syncMatch(match: MatchSummary): Promise<MatchSummary | null> {
  if (match.status === "finished") return null;
  const adapter = gameById(match.gameId)?.adapter ?? null;

  // No profile args: the client is cached per game (warmed at app start), and passing them here
  // would re-sync the profile to the server on every poll. We only need the minted identity.
  const arcade = await getArcade(match.gameId);
  if (match.gameId === "luna-genesis") {
    const view = await arcade.getLunaGenesisState(match.roomId, match.roomToken) as LunaView;
    const me = view.players.find((player) => player.playerId === view.you);
    const opponentScore = Math.max(0, ...view.players.filter((player) => player.playerId !== view.you).map((player) => player.score));
    const result = view.phase === "complete"
      ? view.winnerIds.includes(view.you) ? (view.winnerIds.length > 1 ? "draw" : "won") : "lost"
      : match.result;
    const fields: Partial<MatchSummary> = {
      myScore: me?.score ?? 0,
      opponentScore,
      status: view.phase === "complete" ? "finished" : view.activePlayerId === view.you ? "your-turn" : "waiting",
      result,
    };
    const changed = (Object.keys(fields) as Array<keyof MatchSummary>).some((key) => fields[key] !== match[key]);
    return changed ? { ...match, ...fields, updatedAt: new Date().toISOString() } : null;
  }
  const snap = (await arcade.getTurnState(match.roomId, match.roomToken)) as TurnSnapshot;
  if (snap.state == null) return null;

  let fields: Partial<MatchSummary>;
  if (adapter) {
    const patch = adapter.summarize(adapter.validateState(snap.state), arcade.me);
    fields = {
      myScore: patch.myScore,
      opponentScore: patch.opponentScore,
      status: patch.status,
      result: patch.result === "won" || patch.result === "lost" || patch.result === "draw" || patch.result === "expired" ? patch.result : match.result,
    };
  } else {
    // Games without a rules adapter (e.g. Acquire): once a move exists, Arcade's turn ownership
    // is authoritative even though the state blob is opaque — derive the pill from the active seat.
    fields = { status: snap.turn === arcade.me ? "your-turn" : "waiting" };
  }

  const changed = (Object.keys(fields) as Array<keyof MatchSummary>).some((key) => fields[key] !== match[key]);
  if (!changed) return null;
  return { ...match, ...fields, updatedAt: new Date().toISOString() };
}
